import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';
import { useDebouncedCallback } from 'use-debounce';

import {
  RecommendationsSettings,
  RecoSettingsGoals,
} from '../../../types/Recommendations.js';
import BudgetRelocationAmount from './BudgetRelocationAmount/index.jsx';
import { PriorityChangeBlock } from './PriorityChange/index.js';
import { RecommendationSettingsType } from './RecommendationSettings.types.js';
import {
  isEquals,
  transformServerSettingsToUI,
  transformUISettingsToServer,
} from './utils/index.js';
import { RecosSettingsSchema } from './validators/RecosSettingsSchema.js';

type Props = {
  isReadOnlyUserPermission: boolean;
  settings: RecommendationsSettings;
  recoSettingsPriritisationGoal: RecoSettingsGoals | null;
  onChange: (settings: Partial<RecommendationsSettings>) => void;
  onCloseModal: () => void;
};

const RecommendationSettings: React.FC<Props> = ({
  isReadOnlyUserPermission,
  recoSettingsPriritisationGoal,
  settings,
  onChange,
  onCloseModal,
}) => {
  const { t } = useTranslation('@myn/planning');
  const schema = RecosSettingsSchema(t);
  const defaultValues = transformServerSettingsToUI(settings);

  const [isWarningShown, setIsWarningShown] = useState(
    !!recoSettingsPriritisationGoal,
  );

  const formMethods = useForm<RecommendationSettingsType>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });

  const { watch, handleSubmit } = formMethods;

  const onChangeDebounced = useDebouncedCallback(onChange, 2000);

  useEffect(() => {
    return () => onChangeDebounced.flush();
  }, []);

  useEffect(() => {
    const subscription = watch(() => {
      handleSubmit((validated: any) => {
        if (!isEquals(defaultValues, validated)) {
          onChangeDebounced(transformUISettingsToServer(validated));
        }
      })();
    });

    return () => subscription.unsubscribe();
  }, [defaultValues]);

  return (
    <FormProvider {...formMethods}>
      <RootForm>
        {isWarningShown && recoSettingsPriritisationGoal && (
          <PriorityChangeBlock
            goals={recoSettingsPriritisationGoal}
            onCloseModal={onCloseModal}
            onWarningHide={() => setIsWarningShown(false)}
          />
        )}
        <PrioritisationAndBudgetBlock>
          <BudgetRelocationAmount isEditable={!isReadOnlyUserPermission} />
        </PrioritisationAndBudgetBlock>
      </RootForm>
    </FormProvider>
  );
};

const RootForm = styled('form')`
  padding: ${({ theme }) => theme.spacing(0.625)};
`;

const PrioritisationAndBudgetBlock = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${({ theme }) => theme.spacing(2.5)};
`;

export default RecommendationSettings;
